import { useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { useOrgContext } from "../context/OrgContext";

/**
 * Reads `:orgSlug` from URL params and syncs it with OrgContext.
 * If the slug doesn't match any managed org, redirects to the org picker.
 */
export function OrgSlugGuard({ children }: { children: React.ReactNode }) {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const navigate = useNavigate();
  const { managedOrgs, setOrganization, currentOrgId, isLoading } = useOrgContext();

  useEffect(() => {
    if (isLoading || !orgSlug || managedOrgs.length === 0) return;

    const matchedOrg = managedOrgs.find((org) => org.slug === orgSlug);

    if (!matchedOrg) {
      // Slug doesn't match any managed org → go to org picker
      navigate("/", { replace: true });
      return;
    }

    // Sync org context if it doesn't match the URL slug
    if (currentOrgId !== matchedOrg._id) {
      setOrganization(matchedOrg._id);
    }
  }, [orgSlug, managedOrgs, currentOrgId, isLoading, setOrganization, navigate]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // If orgs loaded but slug doesn't match — redirect is pending via effect
  const matchedOrg = managedOrgs.find((org) => org.slug === orgSlug);
  if (!matchedOrg && managedOrgs.length > 0) {
    return null;
  }

  return <>{children}</>;
}
